import { useEffect, useState } from "react";
import BottomSheet from "../components/BottomSheet";
import { getItemOptions } from "../api/apis";

export default function EditModal({ item, setItem, add }) {
    const [selectedRemovals, setSelectedRemovals] = useState([]);
    const [selectedAddOns, setSelectedAddOns] = useState([]);
    const [selectedSides, setSelectedSides] = useState([]);
    const [specialRequest, setSpecialRequest] = useState("");
    const [addons, setAddons] = useState({ T: [], S: [] });
    const [totalPrice, setTotalPrice] = useState(0);

    // ✅ Fetch available add-ons/sides and initialize selection
    useEffect(() => {
        if (item) {
            setTotalPrice(item.finalPrice ? item.finalPrice / 100 : item.price / 100);
            setSelectedAddOns(item.customizations?.add_ons ?? []);
            setSelectedSides(item.customizations?.sides ?? []);
            setSelectedRemovals(item.customizations?.removed ?? []);
            setSpecialRequest(item.pbo?.plu00?.p0fritxt || ""); // Load existing special request
            fetchAddons(item.mnuid);
        } else {
            setAddons({ T: [], S: [] });
        }
    }, [item]);

    // ✅ Fetch available add-ons and sides from API
    async function fetchAddons(menuId) {
        try {
            const fetchedAddons = await getItemOptions(menuId);
            setAddons(fetchedAddons);
        } catch (error) {
            console.error("Failed to fetch add-ons:", error);
        }
    }

    if (!item) return null; // Prevent rendering if no item is selected

    // ✅ Toggle removable ingredients
    const handleToggleRemoval = (ingredient) => {
        setSelectedRemovals((prev) =>
            prev.includes(ingredient) ? prev.filter((i) => i !== ingredient) : [...prev, ingredient]
        );
    };

    // ✅ Toggle add-ons and update price
    const handleToggleAddOn = (addOn) => {
        setSelectedAddOns((prevSelected) => {
            let updatedAddOns;
            let updatedPrice = totalPrice;

            const isSelected = prevSelected.some(a => a.m0katnr === addOn.m0katnr);

            if (isSelected) {
                // Remove add-on
                updatedAddOns = prevSelected.filter(a => a.m0katnr !== addOn.m0katnr);
                updatedPrice -= addOn.m0pris / 100;
            } else {
                // Add add-on
                updatedAddOns = [...prevSelected, addOn];
                updatedPrice += addOn.m0pris / 100;
            }

            setTotalPrice(updatedPrice);
            return updatedAddOns;
        });
    };

    // ✅ Toggle side orders and update price
    const handleToggleSide = (side) => {
        setSelectedSides((prevSelected) => {
            let updatedSides;
            let updatedPrice = totalPrice;

            const isSelected = prevSelected.some(s => s.m0katnr === side.m0katnr);

            if (isSelected) {
                // Remove side order
                updatedSides = prevSelected.filter(s => s.m0katnr !== side.m0katnr);
                updatedPrice -= side.m0pris / 100;
            } else {
                // Add side order
                updatedSides = [...prevSelected, side];
                updatedPrice += side.m0pris / 100;
            }

            setTotalPrice(updatedPrice);
            return updatedSides;
        });
    };

    // ✅ Finalize edit & update cart
    const handleUpdateCart = () => {
        const customizedItem = {
            ...item,
            pbo: {
                ...item.pbo,
                plu00: {
                    ...item.pbo?.plu00,
                    p0fritxt: specialRequest, // ✅ Store special request in fritext
                }
            },
            customizations: {
                add_ons: selectedAddOns,
                sides: selectedSides,
                removed: selectedRemovals,
            },
            finalPrice: totalPrice * 100,
        };

        add(customizedItem); // Edit existing cart item
        setItem(null); // Close modal
    };

    return (
        <BottomSheet
            headerText="Redigera Artikel"
            saveAction={handleUpdateCart}
            showModal={!!item}
            setShowModal={() => setItem(null)}
            saveText="Spara ändringar"
        >
            <h5 className="mb-3">{item.text.toSwe()}</h5>
            <p className="text-muted">{item.desc.toSwe()}</p>

            {/* ✅ Remove Ingredients */}
            {item?.pbo?.iga?.length > 0 && (
                <div className="mb-3">
                    <h6>Ta bort ingredienser:</h6>
                    {item.pbo.iga
                        .filter((ingredient) => ingredient?.p0text?.trim() !== "")
                        .map((ingredient, index) => (
                            <div key={index} className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`remove-${ingredient.p0lopnr}`}
                                    checked={selectedRemovals.includes(ingredient.p0lopnr)}
                                    onChange={() => handleToggleRemoval(ingredient.p0lopnr)}
                                />
                                <label className="form-check-label" htmlFor={`remove-${ingredient.p0lopnr}`}>
                                    {ingredient.p0text}
                                </label>
                            </div>
                        ))}
                </div>
            )}

            {/* ✅ Available Add-Ons */}
            {addons?.T?.length > 0 && (
                <div className="mb-3">
                    <h6>Lägg till:</h6>
                    {addons.T.map((addOn, index) => (
                        <div key={index} className="form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id={`addon-${addOn.m0katnr}`}
                                checked={selectedAddOns.some(a => a.m0katnr === addOn.m0katnr)}
                                onChange={() => handleToggleAddOn(addOn)}
                            />
                            <label className="form-check-label" htmlFor={`addon-${addOn.m0katnr}`}>
                                {addOn.m0text.toSwe()} (+{(addOn.m0pris / 100).toFixed(2)} kr)
                            </label>
                        </div>
                    ))}
                </div>
            )}

            {/* ✅ Available Side Orders */}
            {addons?.S?.length > 0 && (
                <div className="mb-3">
                    <h6>Side order:</h6>
                    {addons.S.map((side, index) => (
                        <div key={index} className="form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id={`side-${side.m0katnr}`}
                                checked={selectedSides.some(s => s.m0katnr === side.m0katnr)}
                                onChange={() => handleToggleSide(side)}
                            />
                            <label className="form-check-label" htmlFor={`side-${side.m0katnr}`}>
                                {side.m0text.toSwe()} (+{(side.m0pris / 100).toFixed(2)} kr)
                            </label>
                        </div>
                    ))}
                </div>
            )}

            {/* ✅ Special Requests */}
            {/* 
            <div className="mb-3">
                <h6>Andra justeringar:</h6>
                <textarea
                    className="form-control bg-light"
                    rows="2"
                    value={specialRequest}
                    onChange={(e) => setSpecialRequest(e.target.value)}
                ></textarea>
            </div>
            */}

            {/* ✅ Order Summary */}
            <div className="d-flex justify-content-between align-items-center mt-3">
                <h6 className="m-0">Totalbelopp:</h6>
                <h5 className="fw-bold">{totalPrice.toFixed(2)} kr</h5>
            </div>
        </BottomSheet>
    );
}
